import { Text, TextProps } from '@chakra-ui/react'
import { ReactNode } from 'react'

/**
 * Valor ausente numa célula. É a mesma grafia do `<DetailField>`, e por isso o mesmo traço:
 * havia `'—'` (27 sites em 17 arquivos), `'-'` (61 em 15), `'Não informado'` (15 em 11), `'N/A'`
 * (7 em 2) e `'Sem …'` escrito inline — e **duas telas usavam duas grafias dentro do mesmo
 * `<Tbody>`** (`PromoBannersPage` e `SellerSalesModal`).
 */
export const EMPTY_CELL_VALUE = '-'

export interface IEmptyCellProps extends Omit<TextProps, 'children'> {
  /** O valor já formatado. `null`, `undefined` e `''` viram o traço. */
  value?: ReactNode
  /** Texto no lugar do traço, para o vazio que tem nome próprio (`Sem resposta`). */
  emptyValue?: ReactNode
}

/**
 * O valor de uma célula de tabela, com o vazio desenhado de um jeito só — o irmão do
 * `<DetailField>` para o que não é par rótulo/valor.
 *
 * A cor também divergia, e não só o texto: havia `<Text color="gray.400">—</Text>`,
 * `<span style={{ color: '#A0AEC0' }}>—</span>` (hexadecimal cru, que é o `gray.400` do tema
 * escrito à mão) e `<small style={{ color: 'gray' }}>`. Deixar isso para cada `||` do call site
 * é o que garante que o próximo esqueça o cinza — e um traço preto ao lado de um valor preto
 * não diz "não há dado", diz "o dado é um traço".
 *
 * `gray.400` pelo mesmo motivo do `DetailField`: a célula continua na tela em vez de sumir, mas
 * sem competir com as preenchidas.
 */
export default function EmptyCell({ value, emptyValue = EMPTY_CELL_VALUE, ...rest }: IEmptyCellProps) {
  const isEmpty = value == null || value === ''

  return (
    <Text as="span" {...(isEmpty ? { color: 'gray.400' } : {})} {...rest}>
      {isEmpty ? emptyValue : value}
    </Text>
  )
}
